import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { profileAPI } from './api';
import styled from 'styled-components';
import { techColors } from './styles/TechRetroEffects';

const LoadingContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  opacity: 0;
  animation: fadeIn 0.3s ease-in 0.3s forwards;
  pointer-events: none;

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top: 3px solid ${techColors.textPrimary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const PrivateRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsActivation, setNeedsActivation] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      setIsLoading(true);

      try {
        await profileAPI.getProfile();
        if (!cancelled) {
          setIsAuthenticated(true);
          setNeedsActivation(false);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (!error.response) {
          // Transient network failure — keep previous/unknown state, deny access safely
          setIsAuthenticated(false);
        } else if (error.response?.status === 403 && error.response?.data?.error_code === 2003) {
          setNeedsActivation(true);
          setIsAuthenticated(false);
        } else if (error.response?.status === 401 || error.response?.status === 403) {
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading || isAuthenticated === null) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (needsActivation) {
    return <Navigate to="/activate" replace />;
  }

  if (isAuthenticated !== true) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default PrivateRoute;
